//changed due to package upgrade
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, Box, Card, Avatar } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import {
  People as PeopleIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import {
  RoleManagementPage,
  PermissionMatrixGrid,
} from '@projectK/modules/General/RolesAndPermission';
import jwtAxios from '@projectK/services/auth/JWT';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import AppLoader from '@projectK/components/AppLoader';

const RBACDashboard = () => {
  const { user } = useAuthUser();
  const [selectedRole, setSelectedRole] = useState(null);
  const [currentView, setCurrentView] = useState('roles');
  const [mockModules, setMockModules] = useState([]);
  const [mockPermissionTypes, setMockPermissionTypes] = useState([]);
  const [mockModulePermissions, setMockModulePermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [swapPermissions, setSwapPermissions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const handleUpdatePermissions = (roleId, permissionIds) => {
    setRolePermissions({
      ...rolePermissions,
      [roleId]: permissionIds,
    });
  };

  const handlePermissionClick = (roleId) => {
    setSelectedRole(roleId);
    setCurrentView('permissions');
  };

  const handleBackToRoles = () => {
    setCurrentView('roles');
    setSelectedRole(null);
  };

  const rolePermissionCounts = Object.fromEntries(
    Object.entries(rolePermissions).map(([roleId, permissions]) => [
      roleId,
      permissions?.length,
    ]),
  );
  // Fetch roles and permissions based on selected customer
  const fetchData = useMemo(
    () => async () => {
      if (!user?.customerId) return;
      setLoadingRoles(true);
      try {
        const responseRoles = await jwtAxios.get('/roles', {
          params: { customer_id: user?.customerId },
        });
        const responseModules = await jwtAxios.get('/modules');
        const responsePermissions = await jwtAxios.get('/permissions');
        const responseModulePermissions = await jwtAxios.get(
          '/permissions/module-permissions',
        );
        const responseRoleModulePermissions = await jwtAxios.get(
          '/role-module-permissions',
          {
            params: { customer_id: user?.customerId },
          },
        );
        const responseSwapPermissions = await jwtAxios.get(
          "/role-module-permissions/swap-role",
          {
            params: { customer_id: user?.customerId },
          }
        );
        setMockModules(responseModules?.data || []);
        setMockPermissionTypes(responsePermissions?.data || []);
        setMockModulePermissions(responseModulePermissions?.data || []);
        setRoles(
          (responseRoles?.data?.list || []).filter(role => role?.is_active === 1)
        );
        setRolePermissions(responseRoleModulePermissions?.data || {});
        setSwapPermissions(responseSwapPermissions.data || {});
      } catch (error) {
        console.error(error);
        setMockModules([]);
        setMockPermissionTypes([]);
        setMockModulePermissions([]);
        setRoles([]);
        setRolePermissions({});
        setSwapPermissions([]);
      }finally {
        setLoadingRoles(false);
      }
    },
    [user?.customerId],
  );

  useEffect(() => {
    fetchData();
  }, [user?.customerId]);

  return (
    <Grid container spacing={4} sx={{ p: 4 }}>
      {/* Title & Description */}
      <Grid size={{ xs: 12 }} textAlign='center' sx={{ mb: 0 }}>
        <Typography
          variant='h2'
          fontWeight={800}
          sx={{
            color: '#292764',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            fontSize: { xs: 26, md: 34 },
            letterSpacing: 0.3,
            mb: 0.5,
          }}
        >
          <ShieldIcon size={32} style={{ marginRight: 8 }} />
          RBAC Management System
        </Typography>
        <Box
          sx={{
            height: 4,
            width: 56,
            mx: 'auto',
            mt: 0.5,
            mb: 2,
            borderRadius: 2,
            background: 'linear-gradient(90deg, #7c3aed, #2e81f7 80%)',
            boxShadow: '0 2px 8px #6a4ecb2a',
          }}
        />
        <Typography
          variant='subtitle1'
          color='text.secondary'
          sx={{ fontWeight: 500, fontSize: 17, mb: 0.5 }}
        >
          Manage roles and permissions across modules in the application
        </Typography>
      </Grid>

      {/* Summary Cards */}
      {user?.customerId && (
        <Grid  container spacing={3}>
          <SummaryCard
            title='Total Roles'
            icon={<PeopleIcon fontSize='large' />}
            count={roles?.length}
            sub='active'
            colorIndex={0}
          />
          <SummaryCard
            title='Modules'
            icon={<StorageIcon fontSize='large' />}
            count={mockModules?.length}
            sub='System modules'
            colorIndex={1}
          />
          <SummaryCard
            title='Permission Types'
            icon={<SettingsIcon fontSize='large' />}
            count={mockPermissionTypes?.length}
            sub='Operation types'
            colorIndex={2}
          />
        </Grid>
      )}

      {/* Tab View */}
      <Grid size={{ xs: 12 }}>
      {loadingRoles ? (
           <AppLoader/>
        ) : (
      <>
        {user?.customerId &&
          currentView === 'permissions' &&
          mockModulePermissions && (
            <PermissionMatrixGrid
              roles={roles}
              modules={mockModules}
              permissionTypes={mockPermissionTypes}
              permissions={mockModulePermissions}
              rolePermissions={rolePermissions}
              onUpdatePermissions={handleUpdatePermissions}
              selectedRole={selectedRole}
              onBack={handleBackToRoles}
              setSelectedRole={setSelectedRole}
              swapPermissions={swapPermissions}
            />
          )}

        {user?.customerId && currentView === 'roles' && (
          <RoleManagementPage
            roles={roles}
            rolePermissionCounts={rolePermissionCounts}
            onPermissionClick={handlePermissionClick}
            fetchData={fetchData}
          />
        )}
        </>
        )}
      </Grid>
    </Grid>
  );
};

export default RBACDashboard;

const pastelColors = [
  {
    avatarBg: '#f1fcf7',
    iconColor: '#7ed9b8',
    chipBg: '#f5fdf9',
    chipColor: '#7ed9b8',
  },
  {
    avatarBg: '#e8f0ff',
    iconColor: '#8cbfff',
    chipBg: '#f3f7ff',
    chipColor: '#8cbfff',
  },
  {
    avatarBg: '#f4edff',
    iconColor: '#a78bfa',
    chipBg: '#f8f3ff',
    chipColor: '#a78bfa',
  },
];

const SummaryCard = ({ title, icon, count, sub, chip, colorIndex }) => {
  const { avatarBg, iconColor, chipBg, chipColor } =
    pastelColors[colorIndex % pastelColors?.length];

  return (
    <Grid size={{ xs: 12, sm: 6,md: 4,  }}>
      <Card
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          borderRadius: 6,
          minHeight: 90,
          boxShadow: '0 2px 6px rgba(50,60,110,0.08)',
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 2.5,
          '&:hover': {
            boxShadow: '0 6px 18px rgba(50,60,110,0.13)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            backgroundColor: avatarBg,
            color: iconColor,
            mr: 2.5,
          }}
          variant='circular'
        >
          {React.cloneElement(icon, { fontSize: 'large' })}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 1 }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {count}
          </Typography>

          <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        {chip && (
          <Box
            sx={{
              backgroundColor: chipBg,
              color: chipColor,
              borderRadius: 3,
              px: 1.4,
              py: 0.3,
              minWidth: 42,
              fontWeight: 700,
              fontSize: 15,
              textAlign: 'center',
              ml: 2,
              userSelect: 'none',
            }}
          >
            {chip}
          </Box>
        )}
        <Box sx={{ minWidth: 60, textAlign: 'right', ml: 2 }}>
          <Typography sx={{ fontSize: 12, color: '#b0b4bb' }}>{sub}</Typography>
        </Box>
      </Card>
    </Grid>
  );
};

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  count: PropTypes.number.isRequired,
  sub: PropTypes.string,
  colorIndex:PropTypes.number,
  chip:PropTypes.string,
};
